import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import type { PreferredUnitSystem } from '@frontend/api';

import { displayOptions } from 'modules/common/viewer/dxfViewer/data/actions';
import { getDimensionalPrecision } from 'modules/common/viewer/dxfViewer/data/reducer';
import {
  useViewerUnitSystemPreference,
  inferDisplayUnits,
} from 'modules/common/viewer/hooks/useViewerUnitSystemPreference';

import { useAppSelector } from 'store/hooks';

import { useUnitSystemContext } from 'utils/measurement';

const UNIT_SYSTEM_PREFERENCE_KEY = 'dxfViewer:unitSystemPreference';

export const useDisplayOptions = () => {
  const {
    preferredUnitSystem: groupPreferredUnitSystem,
    setPreferredUnitSystem: setGroupPreferredUnitSystem,
  } = useUnitSystemContext();
  const dimensionalPrecision = useAppSelector(getDimensionalPrecision);
  const { preferredUnitSystem, setPreferredUnitSystem } =
    useViewerUnitSystemPreference({
      preferredUnitSystem: groupPreferredUnitSystem,
      setPreferredUnitSystem: setGroupPreferredUnitSystem,
      storageKey: UNIT_SYSTEM_PREFERENCE_KEY,
    });

  const dispatch = useDispatch();
  const receiveDisplayUnits = useCallback(
    (units: { displayUnits: ReturnType<typeof inferDisplayUnits> }) => {
      dispatch(displayOptions.receiveDisplayUnits(units));
    },
    [dispatch],
  );
  const receiveDimensionalPrecision = useCallback(
    (precision: { dimensionalPrecision: number }) => {
      dispatch(displayOptions.receiveDimensionalPrecision(precision));
    },
    [dispatch],
  );

  const handleUnitsChange = (value: PreferredUnitSystem) => {
    if (value !== preferredUnitSystem) {
      receiveDisplayUnits({
        displayUnits: inferDisplayUnits(value),
      });
      setPreferredUnitSystem(value);
    }
  };

  const handlePrecisionChange = (valueAsString: string | number) => {
    const n =
      typeof valueAsString === 'number' ? valueAsString : Number(valueAsString);
    if (!Number.isFinite(n) || n === dimensionalPrecision) {
      return;
    }
    // The spec puts precision in a flat 0–6 range for both unit systems, so a
    // metric drawing is no longer capped at 4 the way the CAD viewer caps it.
    receiveDimensionalPrecision({
      dimensionalPrecision: Math.max(0, Math.min(6, Math.round(n))),
    });
  };

  return {
    preferredUnitSystem,
    dimensionalPrecision,
    handleUnitsChange,
    handlePrecisionChange,
  };
};
